import React, { useState } from 'react'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import WestOutlinedIcon from '@mui/icons-material/WestOutlined'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import HomeIcon from '@mui/icons-material/Home'
import FinalPrintIcon from './assets/icon-print.svg'
import { Button, TextField, Typography, Menu, MenuItem, Divider, SvgIcon } from '@mui/material'
import { makeStyles } from '@mui/styles'
import theme from './theme'
import { DeleteDialog } from './delete-dialog'
import PrintSettingsDialog from './print-settings-dialog'

const PrintIcon = () => {
  return (
    <svg id="secure-print-icon" xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26">
      <path id="secure-print-icon-2" data-name="secure-print-icon" d="M4.526,10.7h0Zm2.531,12.31v-4.6H2.8V11.853A2.759,2.759,0,0,1,5.676,8.977H21.093a2.759,2.759,0,0,1,2.876,2.876v1.5a5,5,0,0,0-.805-.46c-.345-.115-.575-.23-.92-.345v-.69c0-.345,0-.575-.345-.805s-.46-.345-.805-.345H5.676c-.345,0-.575,0-.805.345a1.044,1.044,0,0,0-.345.805v4.832H7.057V14.615h8.629a3.5,3.5,0,0,0-.575.805,2.313,2.313,0,0,0-.46.92H8.783v4.947h6.788c0,.345.23.575.46.92.23.23.345.575.575.805ZM17.987,8.977V5.526h-9.2V8.977H7.057V3.8H19.712V8.977ZM20.518,22.9a4.231,4.231,0,0,1-2.531-1.726,4.947,4.947,0,0,1-.92-3.106v-2.3l3.567-1.726L24.2,15.765v2.3a5.786,5.786,0,0,1-.92,3.106A4.231,4.231,0,0,1,20.748,22.9Zm-.575-2.646,3.106-3.106-.92-.92-2.186,2.186-.92-.92-.92.92,1.726,1.726h0Z" transform="translate(-0.5 -0.37)" />
      <rect id="Rectangle_3376" data-name="Rectangle 3376" width="26" height="26" fill="none"/>
    </svg>
  )
}

const useStyles = makeStyles({
  controls: {
    display: 'inline-flex',
    justifyContent: 'flex-end',
    width: '58%'
  },
  toolbar: {
    alignContent: 'space-around',
    alignItems: 'center',
    background: 'lightgray',
    display: 'flex',
    height: '60px',
    justifyContent: 'flex-end'
  },
  search: {
    marginLeft: '8px',
    display: 'inline-flex',
    justifyContent: 'left',
    width: '42%'
  },
  page: {
    position: 'relative',
    top: 66
  },
  button: {
    backgroundColor: 'white',
    color: theme.palette.primary.main
  }
})

export const Toolbar = (props) => {
  const { 
    search, setSearch, 
    optimizedJobs, selected,
    distinctQueues, pinnedQueue, setPinnedQueue, 
    printJobs, deleteJobs, deleteUnivPrintJobs, refresh,
    openDeleteDialog, setOpenDeleteDialog, 
    setKeep,
    openPrintSettingsDialog, setOpenPrintSettingsDialog, redirectToPrintSettings,
    setOverridePreferences,
    isLoading,
    setIsLoading
  } = props
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const { path } = useRouteMatch()
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  // console.log('optimizedJobs:', optimizedJobs)
  // console.log('distinctQueues:', distinctQueues)
  // console.log('pinnedQueue:', pinnedQueue)
  // release + keep functionality is not supported for the MS universal print feature
  const selectedJobs = Object.values(selected)
  const disableReleaseAndKeep = selectedJobs && selectedJobs.length && selectedJobs.some(job => job?.isUnivPrintJob)
  const allUniversalPrintJobs = selectedJobs && selectedJobs.length && selectedJobs.every(job => job?.isUnivPrintJob)

  function handlePinMenuClose() {
    setAnchorEl(null)
  }

  function handlePinMenuClick(event) {
    setAnchorEl(event.currentTarget)
  }

  async function handlePrintJob () {
    setIsLoading(true)
    await printJobs(optimizedJobs)
      .finally(() => setIsLoading(false))
  }

  return (
    <div className={`${classes.page}`}>
      <div className={`${classes.toolbar}`}>
        {
          location.pathname === '/release2me/jobs' ?
            <div className={`${classes.search}`}>
              <div>
                <Button
                  id='pin-button'
                  size='large'
                  onClick={handlePinMenuClick}
                  variant={ pinnedQueue?.id ? 'contained' : 'outlined'}
                  sx={{ 
                    margin: '9px', 
                    width: '60px',
                    backgroundColor: pinnedQueue?.id ? theme.palette.primary.main : 'white',
                    color: pinnedQueue?.id ? 'white' : theme.palette.primary.main
                  }}
                >
                  <PushPinOutlinedIcon />
                </Button>
                <Menu
                  id='pin-menu'
                  MenuListProps={{ 'aria-labelledby': 'pin-button' }}
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handlePinMenuClose}
                >
                  <MenuItem
                    key={'-2'}
                    selected={false}
                    onClick={() => {
                      setPinnedQueue({})
                      setAnchorEl(null)
                    }}
                  > {t('Unpin queue')}
                  </MenuItem>
                  <Divider/>
                  {
                    distinctQueues.length > 0 && distinctQueues.map((item) => {
                      return (
                        <MenuItem 
                          key={item.id} 
                          selected={item.id === pinnedQueue?.id}
                          onClick={ () => { 
                            setPinnedQueue(item)
                            setAnchorEl(null)
                          }}
                        >
                          {item.name}
                        </MenuItem>
                      )
                    })
                  }
                </Menu>
              </div>
              <TextField
                id="search-input"
                placeholder={t("Search")}
                variant="outlined"
                size="small"
                value={search}
                onChange={(event) => {setSearch(event.target.value)}}
                sx={{
                  background: '#FFFFFF',
                  border: '1px solid #707070',
                  marginRight: '8px',
                  marginTop: '8px',
                  width: '293px',
                  height: '40px',
                  '& .MuiInputBase-root': {
                      borderRadius: '0',
                      border: 'none'
                  }
                }}
              />

              <Button 
                size='large'
                onClick={() => {refresh()}}
                variant='outlined'
                sx={{ 
                  marginY: '9px', 
                  width: '60px',
                  backgroundColor: 'white',
                  color: theme.palette.primary.main
                }}
              >
                <RefreshOutlinedIcon fontSize='medium' />
              </Button>
            </div>
            :
            <div className={`${classes.search}`}>
              <Typography sx={{ fontSize: 24, fontWeight: 'normal', marginLeft: '16px', marginTop: '4px' }} >{t('Print Settings')}</Typography>
            </div>
        }

        {
          location.pathname === '/release2me/jobs' ?
            <div className={`${classes.controls}`}>
              <Button
                size='large'
                onClick={() => history.push('/')}
                variant='outlined'
                sx={{ 
                  margin: '8px', 
                  width: '100px',
                  backgroundColor: 'white',
                  color: theme.palette.primary.main
                }}
              >
                <HomeIcon fontsize='medium' />
              </Button>
              <Button 
                size='large'
                onClick={async () => { setOpenDeleteDialog(true) }}
                variant='outlined'
                sx={{ 
                  margin: '8px', 
                  width: '100px',
                  backgroundColor: 'white',
                  color: theme.palette.primary.main
                }}
                disabled={Object.keys(selected).length === 0}
              >
                <DeleteOutlinedIcon fontSize='medium' />
              </Button>
              <DeleteDialog 
                open={openDeleteDialog} 
                setOpen={setOpenDeleteDialog} 
                deleteJobs={deleteJobs}
                deleteUnivPrintJobs={deleteUnivPrintJobs}
                selected={selected}
              />

              <Button 
                size='large'
                onClick={() => {
                  setKeep(true)
                  if (Object.keys(selected).length > 0) {
                    setOpenPrintSettingsDialog(true)
                  } else {
                    history.push(`${path}/settings`)
                  }
                }}
                variant='outlined'
                sx={{ 
                  margin: '8px', 
                  width: '100px',
                  backgroundColor: 'white',
                  color: theme.palette.primary.main
                }}
                disabled={Object.keys(selected).length === 0 || disableReleaseAndKeep}
              >
                <PrintOutlinedIcon fontSize='medium' />
                { '+' }
                <SaveOutlinedIcon />
              </Button>

              <Button 
                size='large'
                onClick={() => {
                  setKeep(false)
                  if (Object.keys(selected).length === 0) return
                  if (allUniversalPrintJobs) {
                    handlePrintJob()
                  } else {
                    setOpenPrintSettingsDialog(true)
                  }
                }}
                variant='outlined'
                sx={{ 
                  margin: '8px', 
                  width: '100px',
                  backgroundColor: 'white',
                  color: theme.palette.primary.main
                }}
                disabled={Object.keys(selected).length === 0}
              >
                <SvgIcon>
                  <PrintIcon />
                </SvgIcon>
              </Button>
              <PrintSettingsDialog 
                open={openPrintSettingsDialog}
                setOpen={setOpenPrintSettingsDialog}
                optimizedJobs={optimizedJobs}
                printJobs={printJobs}
                redirect={redirectToPrintSettings}
                setOverridePreferences={setOverridePreferences}
              />
            </div>
            :
            <div className={`${classes.controls}`}>
              <Button 
                size='large'
                onClick={() => { 
                  refresh()
                  history.push(`${path}/jobs`) 
                }}
                variant='outlined'
                sx={{ 
                  margin: '8px', 
                  width: '100px',
                  backgroundColor: 'white',
                  color: theme.palette.primary.main
                }}
              >
                <WestOutlinedIcon fontSize='medium'/>
              </Button>

              <Button 
                size='large'
                onClick={handlePrintJob}
                variant='contained'
                sx={{ margin: '8px', width: '100px' }}
                disabled={isLoading}
              >
                <img src={FinalPrintIcon} alt='final print job' />
              </Button>
            </div>
        }
      </div>
    </div>
  )
}